import type { GameContent, HeroId, MapDefinition, Point, WorldState } from '../../../shared/game/world-types.js';
import { isExplored, type FogState } from '../../../shared/game/fog-types.js';
import { PING_SECONDS, type PingKind } from '../../../shared/network/pings.js';
import { PING_GILT, PING_LOOKS, PING_SILVER } from '../ping-looks.js';
import { matches } from '../../input/keymap.js';

/** The chart's square drawing, in pixels; the 3D HUD shows it on an octagonal face (`rendering/hud/hud-chart.ts`). */
export const CHART_SIZE = 1024;
/** How far a pointer may be from a landmark's pin, as a fraction of the drawing, and still pick it. */
const PIN_REACH = .025;
const clamp = (value: number, low: number, high: number) => Math.min(high, Math.max(low, value));
/** The dark of unexplored ground and of the space round the map. */
const UNKNOWN = '#141a1c';
/** The chart's inks, muted to sit with the art direction's earth palette. Each is #rrggbb, so a wash can fade it to transparent. */
const INK = {
  land: '#a8946a', meadow: '#9c8a5e', pine: '#6f7a52', heath: '#8f7556', water: '#4f625e', cliff: '#6c5841', building: '#5b4631',
  road: '#5a4530', trail: '#7a6448', grid: '#3a2c1a1c',
};

/**
 * The map as an old survey, in world metres on a context already turned and clipped to the map: land in tan, woods and heath
 * washed in faded ink that bleeds out at their edges, water, cliffs and buildings, roads and trails, and a grid every 100 m.
 * `pixel` is one drawing pixel in metres. Shared by the HUD chart and the loading screen's map.
 */
export function inkSurvey(ctx: CanvasRenderingContext2D, map: MapDefinition, pixel: number) {
  ctx.fillStyle = INK.land; ctx.fillRect(-map.width / 2, -map.depth / 2, map.width, map.depth);
  for (const district of map.scenery?.districts ?? []) {
    const wash = ctx.createRadialGradient(district.x, district.z, district.radius * .45, district.x, district.z, district.radius);
    const color = district.biome === 'pine' ? INK.pine : district.biome === 'heath' ? INK.heath : INK.meadow;
    wash.addColorStop(0, color); wash.addColorStop(1, `${color}00`);
    ctx.fillStyle = wash; ctx.beginPath(); ctx.arc(district.x, district.z, district.radius, 0, Math.PI * 2); ctx.fill();
  }
  for (const obstacle of map.obstacles) {
    if (obstacle.kind === 'tree' || obstacle.kind === 'bush' || obstacle.kind === 'rock') continue;
    ctx.fillStyle = obstacle.kind === 'water' ? INK.water : obstacle.kind === 'ridge' || obstacle.kind === 'cliff' ? INK.cliff : INK.building;
    ctx.fillRect(obstacle.x - obstacle.width / 2, obstacle.z - obstacle.depth / 2, obstacle.width, obstacle.depth);
  }
  for (const path of map.scenery?.paths ?? []) {
    ctx.strokeStyle = path.kind === 'road' ? INK.road : INK.trail; ctx.lineWidth = (path.kind === 'road' ? 3 : 1.8) * pixel;
    ctx.beginPath(); path.points.forEach((point, i) => { if (i) ctx.lineTo(point.x, point.z); else ctx.moveTo(point.x, point.z); }); ctx.stroke();
  }
  // A survey grid every 100 m.
  ctx.strokeStyle = INK.grid; ctx.lineWidth = 1.5 * pixel; ctx.beginPath();
  for (let x = -Math.floor(map.width / 200) * 100; x <= map.width / 2; x += 100) { ctx.moveTo(x, -map.depth / 2); ctx.lineTo(x, map.depth / 2); }
  for (let z = -Math.floor(map.depth / 200) * 100; z <= map.depth / 2; z += 100) { ctx.moveTo(-map.width / 2, z); ctx.lineTo(map.width / 2, z); }
  ctx.stroke();
}

/**
 * The map chart, drawn into a square canvas turned to match the camera: screen up is world north-west, so north points to the
 * upper right. The whole map fits inside the octagon the HUD sets it in. Like the world, it shows only what the party has explored:
 * the rest, landmarks included, stays dark. The chart only moves the camera. It never changes server movement or reveals rules.
 */
export class CoastChart {
  readonly canvas = document.createElement('canvas');
  /** Shown while the player has heroes in the world. */
  visible = false;
  expanded = false;
  /** The nearest landmark to the view, lower case, or the map's name. */
  location = '';
  /** Where the camera looks, in world metres. */
  cameraFocus: Point = { x: 0, z: 0 };
  /** Bumped whenever the drawing changes, for the texture showing it. */
  revision = 0;
  private context: CanvasRenderingContext2D;
  private background = document.createElement('canvas');
  private lastDraw = 0;
  private veil = document.createElement('canvas');
  private veilRevision = -1;
  private controls = new AbortController();
  private ownHeroes: HeroId[] = [];
  /** The latest fog from the server; until one arrives nothing is known. */
  private fog: FogState | null = null;
  /** World metres to drawing pixels with the whole map in view. */
  private fit: number;
  /** How far the chart is zoomed in (1 shows the whole map) and the world point at its centre. */
  private zoom = 1;
  private center: Point = { x: 0, z: 0 };
  /** World metres to drawing pixels. */
  private get scale() { return this.fit * this.zoom; }
  /** What the background was last drawn for: unfolded or not, the zoom and the centre. */
  private drawnView = '';
  constructor(host: HTMLElement, private content: GameContent, private focus: (point: Point) => void, readonly follow: () => void) {
    this.canvas.width = this.canvas.height = this.background.width = this.background.height = CHART_SIZE;
    this.context = this.canvas.getContext('2d')!;
    // The map's corners must stay inside the octagon (flat sides every 45°, apothem a little inside the drawing's half-width).
    const apothem = CHART_SIZE / 2 * Math.cos(Math.PI / 8) * .95, { width, depth } = content.map;
    let reach = 0;
    for (const [x, z] of [[1, 1], [1, -1], [-1, 1], [-1, -1]]) for (let side = 0; side < 8; side++) {
      const p = this.turn(x * width / 2, z * depth / 2), a = side * Math.PI / 4;
      reach = Math.max(reach, p.x * Math.cos(a) + p.y * Math.sin(a));
    }
    this.fit = apothem / reach;
    this.location = content.map.name;
    host.ownerDocument.addEventListener('keydown', event => {
      if (!this.ownHeroes.length || event.repeat || (event.target as HTMLElement).closest('input, button, textarea, select, summary') || event.ctrlKey || event.metaKey || event.altKey) return;
      if (matches(event, 'findParty')) { event.preventDefault(); follow(); }
      if (matches(event, 'chart')) { event.preventDefault(); this.toggle(); }
    }, { signal: this.controls.signal });
  }
  setOwnHeroes(ids: HeroId[]) { this.ownHeroes = ids; this.visible = !!ids.length; }
  toggle() { this.expanded = !this.expanded; this.lastDraw = 0; }
  dispose() { this.controls.abort(); }

  /** World metres turned to the screen's frame (x right, y down), unscaled. */
  private turn(x: number, z: number) { return { x: (x - z) / Math.SQRT2, y: (x + z) / Math.SQRT2 }; }
  /** A world point on the drawing, in pixels. */
  private point(point: Point) { const p = this.turn(point.x - this.center.x, point.z - this.center.z); return { x: CHART_SIZE / 2 + p.x * this.scale, y: CHART_SIZE / 2 + p.y * this.scale }; }
  /** The world point under a spot on the drawing (`u` right, `v` down, both 0–1). */
  worldAt(u: number, v: number): Point {
    const x = (u - .5) * CHART_SIZE / this.scale, y = (v - .5) * CHART_SIZE / this.scale;
    return { x: this.center.x + (x + y) / Math.SQRT2, z: this.center.z + (y - x) / Math.SQRT2 };
  }
  /**
   * Zooms the chart by `steps` wheel notches (positive zooms in) about a spot on the drawing, which stays where it is. Fully out, the
   * whole map shows again; zoomed in, the centre stays over the map.
   */
  zoomAt(u: number, v: number, steps: number) {
    const zoom = clamp(this.zoom * 1.25 ** steps, 1, 8);
    if (zoom === this.zoom) return;
    const anchor = this.worldAt(u, v), { width, depth } = this.content.map;
    this.center = zoom === 1 ? { x: 0, z: 0 } : {
      x: clamp(anchor.x - (anchor.x - this.center.x) * this.zoom / zoom, -width / 2, width / 2),
      z: clamp(anchor.z - (anchor.z - this.center.z) * this.zoom / zoom, -depth / 2, depth / 2),
    };
    this.zoom = zoom; this.lastDraw = 0;
  }
  /** Slides the drawing by a fraction of its width (`du` right, `dv` down), as a drag would; the centre stays over the map. */
  panBy(du: number, dv: number) {
    const x = du * CHART_SIZE / this.scale, y = dv * CHART_SIZE / this.scale, { width, depth } = this.content.map;
    this.center = { x: clamp(this.center.x - (x + y) / Math.SQRT2, -width / 2, width / 2), z: clamp(this.center.z - (y - x) / Math.SQRT2, -depth / 2, depth / 2) };
    this.lastDraw = 0;
  }
  private get view() { return `${this.expanded}:${this.zoom}:${this.center.x}:${this.center.z}`; }
  /** Whether the party has explored a point (all of it on a development host that reveals the map). */
  private known(point: Point) { return !!this.fog && (!!this.fog.revealed || isExplored(this.content.map, this.fog, point)); }
  /** The landmarks the party has found. */
  private found() { return (this.content.map.landmarks ?? []).filter(landmark => this.known(landmark)); }
  /** The found landmark whose pin is under a spot on the drawing, if any. */
  landmarkAt(u: number, v: number) {
    return this.found().find(landmark => { const p = this.point(landmark); return Math.hypot(p.x / CHART_SIZE - u, p.y / CHART_SIZE - v) < PIN_REACH; }) ?? null;
  }
  /** Looks at the landmark under a spot, or else the ground there. */
  lookAt(u: number, v: number) { this.focus(this.landmarkAt(u, v) ?? this.worldAt(u, v)); }

  /** Draws the map in world metres, turned to the screen; pins and names are drawn upright afterwards. */
  private drawBackground() {
    const ctx = this.background.getContext('2d')!, map = this.content.map;
    ctx.setTransform(1, 0, 0, 1, 0, 0); ctx.fillStyle = UNKNOWN; ctx.fillRect(0, 0, CHART_SIZE, CHART_SIZE);
    ctx.setTransform(1, 0, 0, 1, CHART_SIZE / 2, CHART_SIZE / 2); ctx.scale(this.scale, this.scale); ctx.rotate(Math.PI / 4); ctx.translate(-this.center.x, -this.center.z);
    // Nothing is drawn past the map's edge, where the fog's veil does not reach.
    ctx.save(); ctx.beginPath(); ctx.rect(-map.width / 2, -map.depth / 2, map.width, map.depth); ctx.clip();
    inkSurvey(ctx, map, 1 / this.scale);
    ctx.restore(); ctx.setTransform(1, 0, 0, 1, 0, 0);
    this.drawnView = this.view;
  }
  /** Found landmarks as gilt pins; their names only on the unfolded chart, where they have room. */
  private drawPins() {
    const ctx = this.context, found = this.found().map(landmark => ({ landmark, at: this.point(landmark) }));
    for (const { at: p } of found) {
      ctx.fillStyle = '#e8c27a'; ctx.strokeStyle = '#2a1a0c'; ctx.lineWidth = 2.5;
      ctx.beginPath(); ctx.moveTo(p.x, p.y - 8); ctx.lineTo(p.x + 5.5, p.y); ctx.lineTo(p.x, p.y + 8); ctx.lineTo(p.x - 5.5, p.y); ctx.closePath(); ctx.stroke(); ctx.fill();
    }
    if (!this.expanded) return;
    // Names in dark ink with a parchment halo. Each takes the first free place round its pin (above, below, right, left) and is
    // left out when every place would cover a name already written, so names never overlap.
    ctx.font = '600 20px Dosis'; ctx.lineJoin = 'round'; ctx.textBaseline = 'middle';
    const taken: { x: number; y: number; w: number; h: number }[] = [];
    const overlaps = (box: { x: number; y: number; w: number; h: number }) => taken.some(other => box.x < other.x + other.w && other.x < box.x + box.w && box.y < other.y + other.h && other.y < box.y + box.h);
    for (const { at: p } of found) taken.push({ x: p.x - 6, y: p.y - 9, w: 12, h: 18 });
    for (const { landmark, at: p } of found) {
      const name = landmark.name.split(' · ')[0].toLocaleLowerCase().replace(/(^|\s)\S/g, letter => letter.toLocaleUpperCase());
      const w = ctx.measureText(name).width + 6, h = 22;
      const box = [{ x: p.x - w / 2, y: p.y - 12 - h }, { x: p.x - w / 2, y: p.y + 12 }, { x: p.x + 10, y: p.y - h / 2 }, { x: p.x - 10 - w, y: p.y - h / 2 }]
        .map(spot => ({ ...spot, w, h })).find(spot => !overlaps(spot));
      if (!box) continue;
      taken.push(box);
      ctx.textAlign = 'center'; ctx.lineWidth = 5; ctx.strokeStyle = '#d9c79ecc'; ctx.strokeText(name, box.x + w / 2, box.y + h / 2);
      ctx.fillStyle = '#2a1a0c'; ctx.fillText(name, box.x + w / 2, box.y + h / 2);
    }
    ctx.textBaseline = 'alphabetic';
  }
  /** Unexplored ground is hidden on the chart, as dark as the space round the map; the explored mask is rebuilt only when the server revision changes. */
  private drawVeil(fog: FogState) {
    if (fog.revealed) return;
    if (fog.revision !== this.veilRevision) {
      this.veilRevision = fog.revision;
      this.veil.width = fog.columns; this.veil.height = fog.rows;
      const ctx = this.veil.getContext('2d')!; ctx.fillStyle = UNKNOWN; ctx.fillRect(0, 0, fog.columns, fog.rows);
      for (let run = 0; run < fog.explored.length; run += 2) for (let index = fog.explored[run]; index < fog.explored[run + 1];) {
        const row = Math.floor(index / fog.columns), end = Math.min(fog.explored[run + 1], (row + 1) * fog.columns);
        ctx.clearRect(index % fog.columns, row, end - index, 1); index = end;
      }
    }
    const ctx = this.context, map = this.content.map; ctx.imageSmoothingEnabled = true;
    ctx.setTransform(1, 0, 0, 1, CHART_SIZE / 2, CHART_SIZE / 2); ctx.scale(this.scale, this.scale); ctx.rotate(Math.PI / 4); ctx.translate(-this.center.x, -this.center.z);
    ctx.drawImage(this.veil, -map.width / 2, -map.depth / 2, fog.columns * fog.tileSize, fog.rows * fog.tileSize);
    ctx.setTransform(1, 0, 0, 1, 0, 0);
  }
  /** Pings as rings in the pinging player's colour, with ripples in the kind's colour running out from them and the kind's glyph over them. */
  private drawPings(pings: readonly ChartPing[]) {
    const ctx = this.context;
    for (const ping of pings) {
      const p = this.point(ping.at), fade = Math.max(0, Math.min(1, (PING_SECONDS - ping.age) / .6)), color = `#${PING_LOOKS[ping.kind].color.toString(16).padStart(6, '0')}`;
      ctx.globalAlpha = fade;
      for (let i = 0; i < 3; i++) {
        const life = (ping.age - i * .55) / 1.1;
        if (life <= 0 || life >= 1) continue;
        ctx.globalAlpha = fade * (1 - life) ** 1.5; ctx.strokeStyle = color; ctx.lineWidth = 7;
        ctx.beginPath(); ctx.arc(p.x, p.y, 26 + life * 80, 0, Math.PI * 2); ctx.stroke();
      }
      ctx.globalAlpha = fade; ctx.lineWidth = 12; ctx.strokeStyle = '#1a120b';
      ctx.beginPath(); ctx.arc(p.x, p.y, 24, 0, Math.PI * 2); ctx.stroke();
      ctx.lineWidth = 7; ctx.strokeStyle = `#${(ping.mine ? PING_GILT : PING_SILVER).toString(16)}`; ctx.stroke();
      // The kind's glyph stands over the ring like a pin, so Danger, Rally and the rest read apart at a glance.
      if (ping.glyph) {
        const size = 64, bob = Math.sin(ping.age * 3.2) * 3;
        ctx.drawImage(ping.glyph, p.x - size / 2, p.y - 30 - size + bob, size, size);
      }
    }
    ctx.globalAlpha = 1;
  }
  update(now: number, state: WorldState | null, cameraFocus: Point, view: Point[], pings: readonly ChartPing[] = []) {
    this.cameraFocus = cameraFocus;
    // Pings ripple, so the chart redraws faster while any shows.
    if (now - this.lastDraw < (pings.length ? 33 : 100) || !this.visible) return; this.lastDraw = now;
    const states = state?.heroes ?? this.content.heroes.map(hero => ({ ...hero.spawn, id: hero.id, route: [], health: { dead: false } }));
    this.fog = state?.fog ?? null;
    const landmark = this.found().sort((a, b) => Math.hypot(a.x - cameraFocus.x, a.z - cameraFocus.z) - Math.hypot(b.x - cameraFocus.x, b.z - cameraFocus.z))[0];
    this.location = landmark?.name.toLocaleLowerCase() ?? this.content.map.name;
    // A drawing that would come out the same is not drawn again, so the texture showing it is not uploaded again either.
    const drawing = pings.length ? '' : this.drawingKey(states, state?.enemies ?? [], view);
    if (drawing && drawing === this.drawnKey) return;
    this.drawnKey = drawing;
    if (this.drawnView !== this.view) this.drawBackground();
    const ctx = this.context;
    if (this.fog) { ctx.drawImage(this.background, 0, 0); this.drawVeil(this.fog); }
    else { ctx.fillStyle = UNKNOWN; ctx.fillRect(0, 0, CHART_SIZE, CHART_SIZE); }
    this.drawPins();
    ctx.strokeStyle = '#2a1a0ccc'; ctx.lineWidth = 3; ctx.beginPath(); view.forEach((point, i) => { const p = this.point(point); if (i) ctx.lineTo(p.x, p.y); else ctx.moveTo(p.x, p.y); }); ctx.closePath(); ctx.stroke();
    for (const hero of states) {
      if (hero.health.dead) continue; const own = this.ownHeroes.includes(hero.id), p = this.point(hero);
      if (own && hero.route.length) { ctx.strokeStyle = '#3a2410'; ctx.lineWidth = 2; ctx.beginPath(); ctx.moveTo(p.x, p.y); for (const point of hero.route) { const to = this.point(point); ctx.lineTo(to.x, to.y); } ctx.stroke(); }
      ctx.fillStyle = own ? '#ffdb83' : '#87d8d1'; ctx.strokeStyle = '#1a120b'; ctx.lineWidth = 2;
      ctx.beginPath(); ctx.arc(p.x, p.y, own ? 7 : 5, 0, Math.PI * 2); ctx.fill(); ctx.stroke();
    }
    // The server only sends foes the party can currently see.
    for (const enemy of state?.enemies ?? []) if (!enemy.health.dead) {
      const p = this.point(enemy); ctx.fillStyle = '#a8321f'; ctx.strokeStyle = '#1a0c06'; ctx.lineWidth = 1.5; ctx.fillRect(p.x - 4, p.y - 4, 8, 8); ctx.strokeRect(p.x - 4, p.y - 4, 8, 8);
    }
    this.drawPings(pings);
    this.revision++;
  }
  /** Everything the drawing shows besides pings, to a tenth of a drawing pixel. */
  private drawingKey(heroes: readonly { id: string; x: number; z: number; route: readonly Point[]; health: { dead: boolean } }[], enemies: readonly { x: number; z: number; health: { dead: boolean } }[], view: Point[]) {
    const at = (point: Point) => { const p = this.point(point); return `${Math.round(p.x * 10)},${Math.round(p.y * 10)}`; };
    // The fonts' state too: names written before their font arrived are written again once it has.
    const parts = [this.view, document.fonts?.status ?? '', this.fog ? `${this.fog.revision}:${this.fog.revealed ? 1 : 0}` : '-', this.ownHeroes.join(','), view.map(at).join(' ')];
    for (const hero of heroes) parts.push(hero.health.dead ? `${hero.id}:dead` : `${hero.id}:${at(hero)}:${this.ownHeroes.includes(hero.id) ? hero.route.map(at).join(' ') : ''}`);
    for (const enemy of enemies) if (!enemy.health.dead) parts.push(at(enemy));
    return parts.join('|');
  }
  private drawnKey = '';
}

/** A ping showing on the chart: where it is now, its kind, whose it is and how many seconds it has shown. */
export interface ChartPing { at: Point; kind: PingKind; mine: boolean; age: number; glyph?: CanvasImageSource | null }
